import { ReactNode, useContext, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import DownloadIcon from '@mui/icons-material/Download'
import MenuIcon from '@mui/icons-material/Menu'
import UploadIcon from '@mui/icons-material/Upload'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Loading } from 'src/components/Loading'
import { TextFieldComponent } from 'src/components/TextFieldComponent'
import DataContext from 'src/contexts/DataContext'
import { emitNotificationEvent } from 'src/events'
import {
  TFileStatuses,
  useGetPersonInformationLazy,
  useSaveCandidateDocumentNew,
  useUpdateCandidateDocument,
} from 'src/services/backend'
import * as Yup from 'yup'

import { CandidateModal } from 'src/components/CandidateModal'
import { ErrorText } from 'src/components/ErrorText'
import { composePath } from 'src/utils'
import * as S from './styles'
import { useGetIbgeStates } from 'src/services/ibge'

interface IReserve {
  start_datetime: string
  end_datetime: string
  room: string
  capacity?: number
}

export const Waiting = () => (
  <S.ContainerVerified>
    <S.HourglassEmpty />
    <S.TextSuccess>Aguardando validação</S.TextSuccess>
  </S.ContainerVerified>
)

export const Approved = () => (
  <S.ContainerVerified>
    <S.Verified />
    <S.TextSuccess>Validado</S.TextSuccess>
  </S.ContainerVerified>
)

export const Disapproved = ({ control, errors, observation }: any) => (
  <>
    <S.ContainerVerified>
      <S.CancelError />
      <S.TextError>Recusado - Candidato não atende aos requisitos</S.TextError>
    </S.ContainerVerified>

    <TextFieldComponent
      disabled
      fullWidth
      multiline
      rows={2}
      label="Menssagem da equipe de admissão"
      placeholder="Menssagem da equipe de admissão"
      defaultValue={observation}
    />
  </>
)

const Candidate = () => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  const { registration, setRegistration } = useContext(DataContext)

  const [getIbgeStates, getIbgeStatesResult] = useGetIbgeStates()
  const [saveCandidateDocument, saveCandidateDocumentResult] =
    useSaveCandidateDocumentNew()
  const [updateCandidateDocument, updateCandidateDocumentResult] =
    useUpdateCandidateDocument()
  const [getCandidate, candidateResult] = useGetPersonInformationLazy()

  const getStateAcronyms = () =>
    getIbgeStatesResult.data?.map((state: any) => state.sigla) || []

  const validationScheema = Yup.object().shape({
    uploadFile: Yup.mixed()
      .required('O campo de comprovante de escolaridade é obrigatório')
      .test(
        'is-valid-type',
        "Formato de imagem inválido, utilize '.png', '.jpg', '.jpeg' ou 'pdf'",
        (value: any) => isValidFileType(value?.name?.toLowerCase())
      ),
  })

  const toasts = {
    successToSaveCandidateDocument: () =>
      emitNotificationEvent({
        id: 'successToSaveCandidateDocument',
        type: 'success',
        title: 'Declaração escolar enviada com sucesso!',
        message:
          'Nossa equipe de analistas irá avaliar sua declaração escolar. Volte aqui nas próximas horas para consultar novamente a situação da sua declaração.',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    errorToSaveCandidateDocument: () =>
      emitNotificationEvent({
        id: 'errorToSaveCandidateDocument',
        type: 'error',
        title: 'Ops',
        message:
          'Ocorreu um erro ao salvar o comprovante de escolaridade. Tente novamente ou contate o suporte clicando acima.',
        isIndeterminate: true,
        textButton: 'Fechar erro',
      }),
    errorToUpdateCandidateDocument: (message: string) =>
      emitNotificationEvent({
        id: 'errorToUpdateCandidateDocument',
        type: 'error',
        title: 'Ops',
        message: `Ocorreu um erro ao enviar o comprovante de escolaridade.
          Tente novamente ou contate o suporte clicando acima.
          ${message ? `Erro: ${message}` : ''}`,
        isIndeterminate: true,
        textButton: 'Fechar erro',
      }),
  }

  const defaultValues = {
    uploadFile: undefined,
  }

  const {
    control,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationScheema),
  })

  const isFormValid = () => isValid || !Object.keys(errors)

  const nameImage = (watch('uploadFile') as any)?.name
  const sizeImage = () => {
    let size = (watch('uploadFile') as any)?.size
    let kilobites = Math.round(size / 1024)
    return kilobites.toString() + 'Kb'
  }

  const getStatus = (
    status: TFileStatuses | null
  ): { name: string; icon: ReactNode; status: TFileStatuses | 'Enviar' } => {
    switch (status) {
      case null:
        return {
          name: 'Candidato sem comprovante de escolaridade',
          icon: <S.CancelWarning />,
          status: 'Enviar',
        }

      case 'Em analise':
        return {
          name: 'Aguardando validação',
          icon: <S.HourglassEmpty />,
          status: 'Em analise',
        }
      case 'Aprovado':
        return { name: 'Validado', icon: <S.Check />, status: 'Aprovado' }
      case 'Reprovado':
        return { name: 'Recusado ', icon: <S.Close />, status: 'Reprovado' }
    }
  }

  const isValidFileType = (fileName: string = '') => {
    const validFileExtensions = ['png', 'jpg', 'jpeg']
    return validFileExtensions.indexOf(fileName.split('.').pop() || '') > -1
  }

  const handleDownload = () => {
    window.open(candidateResult.data?.response.file?.fileUrl)
  }

  useEffect(() => {
    getIbgeStates()
  }, [])

  useEffect(() => {
    if (registration.jwt === undefined) {
      navigate(composePath('/candidato/login'))
    }
  }, [registration])

  useEffect(() => {
    if (watch('uploadFile') !== undefined) {
      saveCandidateDocument(watch('uploadFile'))
    }
  }, [watch('uploadFile')])

  useEffect(() => {
    if (saveCandidateDocumentResult?.data !== undefined) {
      updateCandidateDocument(
        registration.jwt!,
        candidateResult.data?.response.candidateId,
        {
          fileName: nameImage,
          fileUrl: saveCandidateDocumentResult?.data?.response?.fileUrl,
          fileId: candidateResult.data?.response.file?.fileId,
        }
      )
    }
  }, [saveCandidateDocumentResult?.data])

  useEffect(() => {
    if (updateCandidateDocumentResult?.data !== undefined) {
      getCandidate(registration.jwt!)
      setRegistration({
        ...registration,
        jwt: registration.jwt,
      })

      toasts.successToSaveCandidateDocument()
    }
  }, [updateCandidateDocumentResult?.data])

  useEffect(() => {
    if (candidateResult?.data) {
      registration.setCandidate(
        setRegistration,
        candidateResult?.data?.response
      )
      reset()
    }
  }, [candidateResult?.data])

  useEffect(() => {
    registration.candidate?.msg
  }, [registration.candidate])

  useEffect(() => {
    updateCandidateDocumentResult?.error &&
      toasts.errorToUpdateCandidateDocument(
        updateCandidateDocumentResult.error.message
      )
  }, [updateCandidateDocumentResult?.error])

  useEffect(() => {
    if (saveCandidateDocumentResult.error) toasts.errorToSaveCandidateDocument()
  }, [saveCandidateDocumentResult.error])

  useEffect(() => {
    getCandidate(registration.jwt!)
    setRegistration({
      ...registration,
      jwt: registration.jwt,
    })
  }, [])

  return (
    <S.ContainerFlex>
      <S.Content>
        <S.ContainerContent>
          <S.Title>Olá, candidato(a)!</S.Title>
          <S.ContainerDescription>
            <S.Typography>
              Código de inscrição
              <p>{candidateResult.data?.response.candidateCode}</p>
            </S.Typography>
          </S.ContainerDescription>
          <S.ContainerDescription>
            <S.Typography>Validação de documentos</S.Typography>
            <S.SubTitle>Comprovante de escolaridade:</S.SubTitle>

            {!!candidateResult.data?.response?.file?.fileId && (
              <S.DocumentContainer>
                <S.DownloadButton onClick={() => handleDownload()}>
                  <S.Link>
                    {candidateResult.data?.response.file.fileName}
                  </S.Link>
                  <S.ButtonIcon>
                    <DownloadIcon />
                  </S.ButtonIcon>
                </S.DownloadButton>

                <S.Status
                  status={
                    getStatus(
                      candidateResult.data?.response.file?.fileStatus || null
                    )?.status
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {
                      getStatus(
                        candidateResult.data?.response.file?.fileStatus || null
                      )?.icon
                    }
                    {
                      getStatus(
                        candidateResult.data?.response.file?.fileStatus || null
                      )?.name
                    }
                  </div>
                  {candidateResult.data?.response.file?.reason ? (
                    <p>({candidateResult.data?.response.file?.reason})</p>
                  ) : (
                    ''
                  )}
                </S.Status>
              </S.DocumentContainer>
            )}

            {candidateResult.data?.response?.file?.fileStatus !==
              'Validado' && (
              <S.ContainerUpload>
                <Controller
                  name="uploadFile"
                  control={control}
                  render={({ field }) => (
                    <S.UploadButton variant="outlined" component="label">
                      <input
                        type="file"
                        accept=".png, .jpg, .jpeg, .pdf"
                        onChange={(event) => {
                          field.onChange(
                            event.currentTarget?.files?.length &&
                              event.currentTarget.files[0]
                          )
                        }}
                        hidden
                      />

                      <S.ButtonIcon>
                        <UploadIcon />
                      </S.ButtonIcon>
                      <S.Link>
                        {<span>Anexar comprovante de escolaridade</span>}
                      </S.Link>
                    </S.UploadButton>
                  )}
                />
                <ErrorText
                  message={errors.uploadFile && errors.uploadFile.message}
                />
              </S.ContainerUpload>
            )}
          </S.ContainerDescription>
        </S.ContainerContent>
      </S.Content>
      <S.Content>
        <S.ContainerContent>
          <S.WarnBox>
            <S.Typography>Aviso importante!</S.Typography>

            {candidateResult.data?.response.msg ? (
              <S.Warning
                dangerouslySetInnerHTML={{
                  __html: candidateResult.data?.response.msg,
                }}
              />
            ) : (
              <S.Text>Sem avisos até o momento</S.Text>
            )}
          </S.WarnBox>
          <S.ContainerDescription>
            <S.Typography>Dados cadastrais</S.Typography>
            <S.Text>
              Para garantir que todas as informações estejam sempre atualizadas,
              recomendamos que você revise os dados informados. Caso precise
              conferir ou realizar alguma alteração, basta clicar no botão
              abaixo:
            </S.Text>
          </S.ContainerDescription>
          <S.ButtonLargeComponent
            type="submit"
            variant={'outlined'}
            endIcon={<MenuIcon />}
            onClick={() => setIsOpen(true)}
          >
            Conferir dados cadastrais
          </S.ButtonLargeComponent>
          <S.ContainerMidias>
            <S.FaceBookIcon />
            <S.InstaIcon />
            <S.TwitteIcon />
            <S.LinkdinIcon />
          </S.ContainerMidias>
        </S.ContainerContent>
      </S.Content>

      <Loading isLoading={saveCandidateDocumentResult?.loading} />

      {isOpen && (
        <CandidateModal
          isOpen={isOpen}
          getStateAcronyms={getStateAcronyms}
          registration={registration}
          onClose={() => setIsOpen(false)}
        />
      )}
    </S.ContainerFlex>
  )
}

export { Candidate }
